import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { TrendingDown, TrendingUp } from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

interface SpendingTrendData {
  id: string;
  periodType: 'daily' | 'weekly' | 'monthly';
  periodDate: string;
  categoryId?: string;
  categoryName?: string;
  totalAmount: number;
  transactionCount: number;
  averageAmount: number;
  percentageChange?: number;
  dayOfWeekPattern: Record<string, number>;
  hourPattern: Record<string, number>;
  topMerchants: Array<{ name: string; amount: number; count: number }>;
}

interface SpendingTrendsChartProps {
  data: SpendingTrendData[];
}

export function SpendingTrendsChart({ data }: SpendingTrendsChartProps) {
  const chartData = data
    .map((item) => ({
      ...item,
      date: parseISO(item.periodDate),
      displayDate: format(parseISO(item.periodDate), 'MMM yyyy', {
        locale: ptBR,
      }),
      shortDate: format(parseISO(item.periodDate), 'MM/yyyy'),
    }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  const formatCurrency = (value: number) => {
    return `R$ ${value.toLocaleString('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`;
  };

  const formatTooltipValue = (value: number, name: string) => {
    if (name === 'totalAmount') {
      return [
        `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
        'Total Gasto',
      ];
    }
    if (name === 'transactionCount') {
      return [`${value}`, 'Transações'];
    }
    if (name === 'averageAmount') {
      return [
        `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
        'Média por Transação',
      ];
    }
    return [value, name];
  };

  // Calculate growth for the latest period
  const latestGrowth = chartData.length >= 2
    ? ((chartData[chartData.length - 1].totalAmount - chartData[chartData.length - 2].totalAmount) / chartData[chartData.length - 2].totalAmount) * 100
    : 0;

  if (!data || data.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        <div className="text-center space-y-2">
          <div className="h-16 w-16 rounded-full bg-muted/50 mx-auto flex items-center justify-center">
            📊
          </div>
          <p>Nenhum dado de tendência disponível</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Key Metrics Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 p-6 text-white">
          <div className="relative z-10">
            <p className="text-blue-100 text-sm font-medium">Total no Período</p>
            <p className="text-2xl font-bold">
              {formatCurrency(chartData.reduce((sum, item) => sum + item.totalAmount, 0))}
            </p>
          </div>
          <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white/10" />
        </div>

        <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 p-6 text-white">
          <div className="relative z-10">
            <p className="text-emerald-100 text-sm font-medium">Média Mensal</p>
            <p className="text-2xl font-bold">
              {formatCurrency(chartData.reduce((sum, item) => sum + item.totalAmount, 0) / Math.max(chartData.length, 1))}
            </p>
          </div>
          <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white/10" />
        </div>

        <div className={`relative overflow-hidden rounded-xl p-6 text-white ${
          latestGrowth >= 0
            ? 'bg-gradient-to-br from-red-500 to-red-600'
            : 'bg-gradient-to-br from-green-500 to-green-600'
        }`}>
          <div className="relative z-10">
            <p className={`text-sm font-medium ${latestGrowth >= 0 ? 'text-red-100' : 'text-green-100'}`}>
              Variação Recente
            </p>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold">
                {Math.abs(latestGrowth).toFixed(1)}%
              </p>
              {latestGrowth >= 0 ? (
                <TrendingUp className="h-5 w-5" />
              ) : (
                <TrendingDown className="h-5 w-5" />
              )}
            </div>
          </div>
          <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white/10" />
        </div>
      </div>

      {/* Main Trend Chart with Area */}
      <div className="rounded-xl border bg-card p-6">
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Evolução dos Gastos</h3>
          <p className="text-muted-foreground text-sm">Tendência temporal dos seus gastos</p>
        </div>
        <div className="h-80">
          <ResponsiveContainer height="100%" width="100%">
            <AreaChart
              data={chartData}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 20,
              }}
            >
              <defs>
                <linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.3} />
              <XAxis
                dataKey="shortDate"
                tick={{ fontSize: 12, fill: 'hsl(var(--foreground))' }}
                axisLine={{ stroke: 'hsl(var(--border))' }}
              />
              <YAxis
                tick={{ fontSize: 12, fill: 'hsl(var(--foreground))' }}
                tickFormatter={formatCurrency}
                axisLine={{ stroke: 'hsl(var(--border))' }}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="rounded-lg border bg-background p-3 shadow-lg">
                        <p className="font-medium">{`Período: ${label}`}</p>
                        {payload.map((entry, index) => (
                          <p key={index} className="text-sm" style={{ color: entry.color }}>
                            {`${entry.name}: R$ ${entry.value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                          </p>
                        ))}
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Area
                type="monotone"
                dataKey="totalAmount"
                stroke="hsl(var(--primary))"
                strokeWidth={3}
                fillOpacity={1}
                fill="url(#colorAmount)"
                dot={{ r: 5, strokeWidth: 2, fill: 'hsl(var(--background))' }}
                activeDot={{ r: 7, strokeWidth: 2, fill: 'hsl(var(--primary))' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Secondary Metrics */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Transaction Count Chart */}
        <div className="rounded-xl border bg-card p-6">
          <div className="mb-4">
            <h4 className="font-semibold">Número de Transações</h4>
            <p className="text-muted-foreground text-sm">Frequência de transações por período</p>
          </div>
          <div className="h-48">
            <ResponsiveContainer height="100%" width="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.3} />
                <XAxis
                  dataKey="shortDate"
                  tick={{ fontSize: 11, fill: 'hsl(var(--foreground))' }}
                  axisLine={{ stroke: 'hsl(var(--border))' }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'hsl(var(--foreground))' }}
                  axisLine={{ stroke: 'hsl(var(--border))' }}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="rounded-lg border bg-background p-3 shadow-lg">
                          <p className="font-medium">{`Período: ${label}`}</p>
                          <p className="text-sm text-emerald-600">
                            {`Transações: ${payload[0]?.value}`}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Bar
                  dataKey="transactionCount"
                  fill="hsl(var(--primary))"
                  radius={[4, 4, 0, 0]}
                  opacity={0.8}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Average Amount Chart */}
        <div className="rounded-xl border bg-card p-6">
          <div className="mb-4">
            <h4 className="font-semibold">Valor Médio por Transação</h4>
            <p className="text-muted-foreground text-sm">Ticket médio dos seus gastos</p>
          </div>
          <div className="h-48">
            <ResponsiveContainer height="100%" width="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.3} />
                <XAxis
                  dataKey="shortDate"
                  tick={{ fontSize: 11, fill: 'hsl(var(--foreground))' }}
                  axisLine={{ stroke: 'hsl(var(--border))' }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: 'hsl(var(--foreground))' }}
                  tickFormatter={(value) => `R$ ${value.toFixed(0)}`}
                  axisLine={{ stroke: 'hsl(var(--border))' }}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <div className="rounded-lg border bg-background p-3 shadow-lg">
                          <p className="font-medium">{`Período: ${label}`}</p>
                          <p className="text-sm text-orange-600">
                            {`Média: R$ ${payload[0]?.value?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                          </p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Line
                  dataKey="averageAmount"
                  stroke="#f59e0b"
                  strokeWidth={3}
                  dot={{ r: 4, strokeWidth: 2, fill: 'hsl(var(--background))' }}
                  activeDot={{ r: 6, strokeWidth: 2, fill: '#f59e0b' }}
                  type="monotone"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Day of Week Pattern */}
      {chartData.length > 0 &&
        chartData[chartData.length - 1].dayOfWeekPattern &&
        Object.keys(chartData[chartData.length - 1].dayOfWeekPattern).length > 0 && (
          <div className="rounded-xl border bg-card p-6">
            <div className="mb-4">
              <h4 className="font-semibold">Padrão por Dia da Semana</h4>
              <p className="text-muted-foreground text-sm">Seu comportamento de gasto durante a semana</p>
            </div>
            <div className="h-48">
              <ResponsiveContainer height="100%" width="100%">
                <BarChart
                  data={Object.entries(
                    chartData[chartData.length - 1].dayOfWeekPattern
                  ).map(([day, amount]) => ({
                    day: day.slice(0, 3),
                    amount: Number(amount),
                  }))}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" strokeOpacity={0.3} />
                  <XAxis
                    dataKey="day"
                    tick={{ fontSize: 12, fill: 'hsl(var(--foreground))' }}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: 'hsl(var(--foreground))' }}
                    tickFormatter={(value) => `R$ ${value.toFixed(0)}`}
                    axisLine={{ stroke: 'hsl(var(--border))' }}
                  />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="rounded-lg border bg-background p-3 shadow-lg">
                            <p className="font-medium">{`${label}`}</p>
                            <p className="text-sm text-purple-600">
                              {`Gasto: R$ ${Number(payload[0]?.value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                            </p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar
                    dataKey="amount"
                    fill="#8b5cf6"
                    radius={[6, 6, 0, 0]}
                    opacity={0.8}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

      {/* Recent Periods Summary */}
      <div className="rounded-xl border bg-card p-6">
        <div className="mb-4">
          <h4 className="font-semibold">Resumo dos Últimos Períodos</h4>
          <p className="text-muted-foreground text-sm">Comparação dos gastos recentes</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {chartData.slice(-3).map((item, index) => (
            <div className="rounded-lg border bg-muted/30 p-4" key={item.id}>
              <div className="text-muted-foreground text-sm mb-1">
                {item.displayDate}
              </div>
              <div className="font-bold text-xl mb-2">
                {formatCurrency(item.totalAmount)}
              </div>
              {item.percentageChange !== undefined && (
                <div className={`flex items-center gap-1 text-sm ${
                  item.percentageChange >= 0 ? 'text-red-500' : 'text-green-500'
                }`}>
                  {item.percentageChange >= 0 ? (
                    <TrendingUp className="h-4 w-4" />
                  ) : (
                    <TrendingDown className="h-4 w-4" />
                  )}
                  {Math.abs(item.percentageChange).toFixed(1)}%
                </div>
              )}
              <div className="text-muted-foreground text-xs mt-1">
                {item.transactionCount} transações
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}